import { Collapse, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select,  Switch } from "@mui/material";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { FormProvider, Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IBookingsApiFilter } from "../../../../api/types";
import { FormAutocomplete, FormDateTimePicker, FormNumberInput, FormTextField } from "../../../../components/fields";
import FilterActions from "../../../../components/filter/filter-actions/FilterActions";
import FilterWrapper from "../../../../components/filter/filter-wrapper/FilterWrapper";
import { ReactQueryKey, VehicleType } from "../../../../types";
import { BOOLEAN_OPTIONS, LOCALE_DATE_TIME_FORMAT } from "../../../../utils";
import { bookingsFilterState } from "../../recoil";
import { IFormValuesBookingsFilter } from "../../types";
import { FormAcrissList, FormAncillaries, FormCategories, FormDropoffLocations, FormPickupLocations, FormProducts, FormRates, FormSalesChannels, FormSegments, FormSippList, FormStatusList, FormSubstatusList } from "./fields";
import { useQuery } from "@tanstack/react-query";
import { getBookingDomains } from "../../../../api/services";
import TenantForm from "../../../../components/multitenant/TenantForm";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import DealerForm from "../../../../components/multitenant/DealerForm";

const FilterBookings = () => {

    const [searchParams] = useSearchParams();

    const { t } = useTranslation('booking');
    const { globalData } = useContext(GlobalContext);

    const setBookingsFilter = useSetRecoilState(bookingsFilterState);

    const domainsQuery = useQuery({
        queryKey: [ReactQueryKey.BookingDomains],
        queryFn: getBookingDomains,
        enabled: false
    });

    const validationSchema = yup.object({
        bookingDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testBookingDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.bookingDateTo ||
                        value <= context.parent.bookingDateTo
                    );
                }
            ),
        bookingDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testBookingDateTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.bookingDateFrom ||
                        value >= context.parent.bookingDateFrom
                    );
                }
            ),
        pickupDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testPickupDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.pickupDateTo ||
                        value <= context.parent.pickupDateTo
                    );
                }
            ),
        pickupDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testPickupDateTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.pickupDateFrom ||
                        value >= context.parent.pickupDateFrom
                    );
                }
            ),
        dropoffDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testDropoffDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dropoffDateTo ||
                        value <= context.parent.dropoffDateTo
                    );
                }
            ),
        dropoffDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testDropoffDateTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dropoffDateFrom ||
                        value >= context.parent.dropoffDateFrom
                    );
                }
            ),
        effectiveBookingDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testEffectiveBookingDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.effectiveBookingDateTo ||
                        value <= context.parent.effectiveBookingDateTo
                    );
                }
            ),
            effectiveBookingDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testEffectiveBookingDateTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.effectiveBookingDateFrom ||
                        value >= context.parent.effectiveBookingDateFrom
                    );
                }
            ),
            tenants: yup
            .array()
            .min(1, "message.validation.required")
    });

    const form = useForm<IFormValuesBookingsFilter>({
        defaultValues: {
            pickupLocations: [],
            dropoffLocations: [],
            statusList: [],
            substatusList: [],
            bookingDateFrom: null,
            bookingDateTo: null,
            pickupDateFrom: null,
            pickupDateTo: null,
            dropoffDateFrom: null,
            dropoffDateTo: null,
            effectiveBookingDateFrom: null,
            effectiveBookingDateTo: null,
            createdBy: '',
            rentalDaysFrom: 0,
            rentalDaysTo: 0,
            bookingNumber: '',
            customerCode: '',
            dossierNumber: '',
            driverFirstName: '',
            driverLastName: '',
            bookingOwnerFirstName: '',
            bookingOwnerLastName: '',
            bookingOwnerCompanyName: '',
            vehicleTypes: [],
            acrissList: [],
            sippList: [],
            segments: [],
            categories: [],
            products: [],
            rates: [],
            subscriptionNumber: '',
            renewal: null,
            licensePlateNumber: '',
            replacementPlateNumber: '',
            substitution: null,
            ancillaries: [],
            rentwayNumber: '',
            affiliationCode: '',
            salesChannels: [],
            customerId: searchParams.get('customerId') ?? '',
            customerAtDesk: true,
            hideDrafts: true,
            recovery: null,
            carSwap: null,
            packageSwap: null,
            businessAgreements: [],
            tenants: globalData?.user.legalEntities?.map((elem: any) => elem.name),
            dealers: globalData?.user.dealers?.map((elem: any) => elem.name),
            keyless: ''
        },
        resolver: yupResolver(validationSchema, {}, { rawValues: true })
    });

    const [expanded, setExpanded] = useState<boolean>(false);

    const tenantsWatch = useWatch<IFormValuesBookingsFilter, 'tenants'>({
        control: form.control,
        name: "tenants"
    });

    const createBookingsFilter = useCallback((data: IFormValuesBookingsFilter): IBookingsApiFilter => ({
        pickupLocations: data.pickupLocations.map(pl => pl.id),
        dropoffLocations: data.dropoffLocations.map(dl => dl.id),
        pickupDateFrom: data.pickupDateFrom?.format(LOCALE_DATE_TIME_FORMAT) || null,
        pickupDateTo: data.pickupDateTo?.format(LOCALE_DATE_TIME_FORMAT) || null,
        dropoffDateFrom: data.dropoffDateFrom?.format(LOCALE_DATE_TIME_FORMAT) || null,
        dropoffDateTo: data.dropoffDateTo?.format(LOCALE_DATE_TIME_FORMAT) || null,
        statuses: data.statusList.map(s => s.code),
        substatuses: data.substatusList.map(s => s.code),
        bookingDateFrom: data.bookingDateFrom?.toISOString() || null,
        bookingDateTo: data.bookingDateTo?.toISOString() || null,
        effectiveBookingDateFrom: data.effectiveBookingDateFrom?.toISOString() || null,
        effectiveBookingDateTo: data.effectiveBookingDateTo?.toISOString() || null,
        createdBy: data.createdBy || null,
        bookingNumber: data.bookingNumber || null,
        rentalDaysFrom: data.rentalDaysFrom || null,
        rentalDaysTo: data.rentalDaysTo || null,
        customerCode: data.customerCode || null,
        businessAgreements: data.businessAgreements?.filter(b => !!b.trim()).map(b => b.trim()),
        dossierNumber: data.dossierNumber || null,
        driverFirstName: data.driverFirstName || null,
        driverLastName: data.driverLastName || null,
        bookingOwnerFirstName: data.bookingOwnerFirstName || null,
        bookingOwnerLastName: data.bookingOwnerLastName || null,
        bookingOwnerCompanyName: data.bookingOwnerCompanyName || null,
        renewal: data.renewal?.value !== undefined ? data.renewal.value : null,
        vehicleTypes: data.vehicleTypes.map(vt => vt.code),
        acrissList: data.acrissList.map(a => a),
        sippList: data.sippList.map(s => s),
        categories: data.categories.map(c => c.code),
        segments: data.segments.map(s => s.code),
        products: data.products.map(p => p.code),
        rates: data.rates.map(r => r.code),
        subscriptionNumber: data.subscriptionNumber || null,
        licensePlateNumber: data.licensePlateNumber || null,
        replacementPlateNumber: data.replacementPlateNumber || null,
        substitution: data.substitution?.value !== undefined ? data.substitution.value : null,
        ancillaries: data.ancillaries.map(a => a.ancillaryCode),
        rentwayNumber: data.rentwayNumber || null,
        affiliationCode: data.affiliationCode || null,
        salesChannels: data.salesChannels.map(sc => sc.code),
        customerId: data.customerId || null,
        customerAtDesk: data.customerAtDesk || false,
        hideDrafts: data.hideDrafts || false,
        recovery: data.recovery?.value !== undefined ? data.recovery.value : null,
        carSwap: data.carSwap?.value !== undefined ? data.carSwap.value : null,
        packageSwap: data.packageSwap?.value !== undefined ? data.packageSwap.value : null,
        tenants: data.tenants || [],
        dealers: data.dealers,
        keyless: data.keyless === '' ? undefined : Boolean(data.keyless)
    }), []);
    const handleSearch = useCallback((data: IFormValuesBookingsFilter) => {
        setBookingsFilter(createBookingsFilter(data));
        setExpanded(false);
    }, []);
    const handleReset = useCallback(() => {
        form.reset();
        setBookingsFilter(createBookingsFilter(form.getValues()));
        setExpanded(false);
    }, []);
    const handleToggle = useCallback(() => {
        setExpanded(current => !current);
    }, [setExpanded]);

    useEffect(() => {
       
        if(Array.from(searchParams).length == 0) return;
        
        form.setValue('subscriptionNumber', searchParams?.get('subscriptionNumber'));
        // @ts-ignore
        if(!!searchParams?.get('hideDrafts') && (/false/i).test(searchParams?.get('hideDrafts'))) form.setValue('hideDrafts', false);
        // @ts-ignore
        if(!!searchParams?.get('customerAtDesk') && (/false/i).test(searchParams?.get('customerAtDesk'))) form.setValue('customerAtDesk', false);
        if((!!searchParams?.get('excludeSubStatuses') || !!searchParams?.get('excludeStatuses')) && domainsQuery?.data) {
            // @ts-ignore
            form.setValue('substatusList', domainsQuery?.data?.substatuses.filter(a => a.code !== searchParams?.get('excludeSubStatuses')));
            // @ts-ignore
            form.setValue('statusList', domainsQuery?.data?.statuses.filter(a => a.code !== searchParams?.get('excludeStatuses')));
        }
        setBookingsFilter(createBookingsFilter(form.getValues()));
        
    }, [domainsQuery?.data]);

    useEffect(() => {

        if(Array.from(searchParams).length == 0) return;
            domainsQuery.refetch();

    },[searchParams])

    return (
        <FilterWrapper>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleSearch)}>
                    <Grid container columnSpacing={5} alignItems="center">
                        <Grid item xs={12} md={6} lg={3}>
                            <FormTextField<IFormValuesBookingsFilter>
                                name="bookingNumber"
                                label={t('label.bookingNumber')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FormTextField<IFormValuesBookingsFilter>
                                name="dossierNumber"
                                label={t('label.dossierNumber')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FormTextField<IFormValuesBookingsFilter>
                                name="rentwayNumber"
                                label={t('label.rentwayNumber')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <FilterActions
                                expanded={expanded}
                                onSearch={form.handleSubmit(handleSearch)}
                                onReset={handleReset}
                                onToggle={handleToggle}
                            />
                        </Grid>
                    </Grid>
                    <Collapse in={expanded} timeout="auto" sx={{ width: '100%' }}>
                        <Grid container columnSpacing={5} rowSpacing={2} pt={2}>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormPickupLocations />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesBookingsFilter>
                                    name="pickupDateFrom"
                                    label={t('label.pickupDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesBookingsFilter>
                                    name="pickupDateTo"
                                    label={t('label.pickupDateTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAcrissList />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDropoffLocations />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesBookingsFilter>
                                    name="dropoffDateFrom"
                                    label={t('label.dropoffDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesBookingsFilter>
                                    name="dropoffDateTo"
                                    label={t('label.dropoffDateTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormSippList />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="driverFirstName"
                                    label={t('label.mainDriverFirstName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="driverLastName"
                                    label={t('label.mainDriverLastName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormStatusList />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormSubstatusList />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesBookingsFilter>
                                    name="bookingDateFrom"
                                    label={t('label.planetCreationDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesBookingsFilter>
                                    name="bookingDateTo"
                                    label={t('label.planetCreationDateTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormNumberInput<IFormValuesBookingsFilter>
                                    name="rentalDaysFrom"
                                    label={t('label.rentalDaysFrom')}
                                    minValue={0}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormNumberInput<IFormValuesBookingsFilter>
                                    name="rentalDaysTo"
                                    label={t('label.rentalDaysTo')}
                                    minValue={0}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesBookingsFilter>
                                    name="effectiveBookingDateFrom"
                                    label={t('label.effectiveBookingDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormDateTimePicker<IFormValuesBookingsFilter>
                                    name="effectiveBookingDateTo"
                                    label={t('label.effectiveBookingDateTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="bookingOwnerFirstName"
                                    label={t('label.bookingOwnerFirstName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="bookingOwnerLastName"
                                    label={t('label.bookingOwnerLastName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="bookingOwnerCompanyName"
                                    label={t('label.bookingOwnerCompanyName')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAutocomplete<IFormValuesBookingsFilter>
                                    name="vehicleTypes"
                                    label={t('label.vehicleType')}
                                    options={[
                                        {
                                            code: VehicleType.CAR,
                                            label: t('label.car')
                                        },
                                        {
                                            code: VehicleType.VAN,
                                            label: t('label.van')
                                        }
                                    ]}
                                    isOptionEqualToValue={(option, value) => option?.code === value?.code}
                                    multiple
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="licensePlateNumber"
                                    label={t('label.licensePlateNumber')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="replacementPlateNumber"
                                    label={t('label.replacementPlateNumber')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormSegments />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormCategories />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormProducts />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormRates />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="subscriptionNumber"
                                    label={t('label.subscriptionNumber')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAutocomplete<IFormValuesBookingsFilter>
                                    name="renewal"
                                    label={t('label.renewal')}
                                    options={BOOLEAN_OPTIONS}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAutocomplete<IFormValuesBookingsFilter>
                                    name="substitution"
                                    label={t('label.substitution')}
                                    options={BOOLEAN_OPTIONS}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}

                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAncillaries />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="createdBy"
                                    label={t('label.createdBy')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAutocomplete<IFormValuesBookingsFilter>
                                    name="businessAgreements"
                                    label={t('label.businessAgreement')}
                                    options={[]}
                                    blurOnSelect={false}
                                    multiple
                                    freeSolo
                                    isOptionEqualToValue={(option, value) => option?.trim() === value?.trim()}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="affiliationCode"
                                    label={t('label.affiliationCode')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormSalesChannels />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormTextField<IFormValuesBookingsFilter>
                                    name="customerId"
                                    label={t('label.customerId')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAutocomplete<IFormValuesBookingsFilter>
                                    name="recovery"
                                    label={t('label.recovery')}
                                    options={BOOLEAN_OPTIONS}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAutocomplete<IFormValuesBookingsFilter>
                                    name="carSwap"
                                    label={t('label.carSwapFilter')}
                                    options={BOOLEAN_OPTIONS}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <FormAutocomplete<IFormValuesBookingsFilter>
                                    name="packageSwap"
                                    label={t('label.packageSwapFilter')}
                                    options={BOOLEAN_OPTIONS}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3} sx={{marginTop: "auto"}}>
                                <Controller
                                    control={form.control}
                                    name={"keyless"}
                                    render={({field}) => (
                                        <FormControl variant="standard" sx={{ width: '100%' }}>
                                            <InputLabel id="keyless-label">{t("label.keyless")}</InputLabel>
                                            <Select
                                                labelId="keyless-label"
                                                id="keyless-label"
                                                value={field.value}
                                                onChange={(event) => form.setValue('keyless', event.target.value)}
                                            >
                                                <MenuItem value={''}></MenuItem>
                                                <MenuItem value={0}>{t("label.no")}</MenuItem>
                                                <MenuItem value={1}>{t("label.yes")}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                            {!(globalData?.user.legalEntities.length === 1 && globalData?.user.dealers.length === 1) &&
                                <Grid item xs={12} md={6} lg={3}>
                                    <TenantForm
                                        control={form.control}
                                        name="tenants"
                                        onChange={() => form.setValue("dealers", [])}
                                    />
                                </Grid>
                            }
                            {!(globalData?.user.legalEntities.length === 1 && globalData?.user.dealers.length === 1) &&
                                <Grid item xs={12} md={6} lg={3}>
                                    <DealerForm
                                        control={form.control}
                                        name="dealers"
                                        tenants={tenantsWatch}
                                    />
                                </Grid>
                            }
                            <Grid item xs={12} md={6} lg={3} sx={{marginTop: "auto"}}>
                                <Controller
                                    control={form.control}
                                    name={"hideDrafts"}
                                    render={({field}) => (
                                        <FormControlLabel sx={{margin: "auto"}}
                                                          control={
                                                              <Switch checked={field.value} sx={{marginLeft: "8px"}}
                                                                      onChange={(e) => {
                                                                          field.onChange(e);
                                                                      }}
                                                                      name="hideDraft"/>
                                                          }
                                                          label={t('label.hideDraft')}
                                                          labelPlacement="start"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3} sx={{marginTop: "auto"}}>
                                <Controller
                                    control={form.control}
                                    name={"customerAtDesk"}
                                    render={({field}) => (
                                        <FormControlLabel sx={{margin: "auto"}}
                                                          control={
                                                              <Switch checked={field.value} sx={{marginLeft: "8px"}}
                                                                      onChange={(e) => {
                                                                          field.onChange(e);
                                                                      }}
                                                                      name="customerAtDesk"/>
                                                          }
                                                          label={t('label.customerAtDesk')}
                                                          labelPlacement="start"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                </form>
            </FormProvider>
        </FilterWrapper>
    );
};

export default FilterBookings;
